import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom"; 
import { Button } from "../../components/candidate-dashboard/ui/button";
import { TestInstructions } from "../../components/candidate-dashboard/test/test-instructions";
import { MCQQuestion } from "../../components/candidate-dashboard/test/mcq-question";
import { Progress } from "../../components/candidate-dashboard/ui/progress";
import tokenService from "../../services/token-service";
import axios from "axios";
import Swal from 'sweetalert2';
import apiUrls from "../../config/api-urls";
import { useNavigate } from "react-router-dom";


export default function InitialTest() {
  const [started, setStarted] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [testData, setTestData] = useState({
    questions: [],
    totalQuestions: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const questionsPerPage = 10;
  const token = tokenService.getToken();
  const navigate = useNavigate();


  const fetchTest = async () => {
    try {
      const response = await axios.get(apiUrls.tests.getTest, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          page: currentPage + 1,
          limit: questionsPerPage,
          testType:'initial',
          jobId: localStorage.getItem("jobId")
        },
      });
         const { data, pagination } = response.data;

    if (!data || data.length === 0) {
      setError("No test found for this job and type.");
      return;
    }
      const test = data[0];
      const { questions, title, _id } = test;
      setTestData({
        _id,
        title,
        questions,
        totalQuestions: questions.length,
        totalPages: pagination.totalPages,
        currentPage: pagination.page,
      });
      setError(null);
    } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch test");
    } finally {
        setLoading(false);
    }
  };
  
  useEffect(() => {
    if (started) {
      fetchTest();
    }
  }, [started, currentPage]);

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: answer, 
    }));
  };

  const handleNextPage = () => {
    if (currentPage < (testData?.totalPages || 1) - 1) {
      setCurrentPage((prev) => prev + 1);
    } else {
      showSubmissionConfirmation();
    }
  };

  const handleSubmit = async () => {
    try {
        const jobId = localStorage.getItem("jobId");
        const jobApplicationId = localStorage.getItem("jobApplicationId");
      const payload = {
        testId: testData._id,
            jobId,
            jobApplicationId,
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          questionId,
          answer,
        })),
      };
      await axios.post(apiUrls.tests.submitInitialTest, payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSubmitted(true);
      navigate("/dashboard", { replace: true });
      setLoading(false)
     window.location.reload();
    } catch (err) {
      setError(err.response?.data?.message || "Submission failed");
    }
  };
  const startNewTest = () => {
    setStarted(true); 
  };

const showSubmissionConfirmation  = () => {
  Swal.fire({
    title: 'Submit Initial Test?',
    text: "Are you sure you want to submit your initial test? This action cannot be undone.",
    icon: 'question',
    width: 600,
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, submit it!',
    cancelButtonText: 'Cancel',
    customClass: {
      popup: 'rounded-lg shadow-xl',
      confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded',
      cancelButton: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded'
    }
  }).then((result) => {
    if (result.isConfirmed) {
      handleSubmit();
    }
  });
};

  if (!started) {
    return (
      <TestInstructions onStart={() => startNewTest(true)} testType="initial" />
    );
  }

  if (loading) {
    return <div className="text-center text-gray-700 dark:text-gray-200">Loading test...</div>;
  }

  if (error) {
    return (
      <div className="text-center text-red-600 dark:text-red-400">
        <p className="mb-2">Error: {error}</p>
        <Button onClick={() => setCurrentPage(0)} className="bg-gray-700 dark:bg-gray-200 dark:text-gray-900">Retry</Button>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="text-center space-y-4">
        <Link
         to="/training"
         className="px-4 py-2 bg-[#EFA938] text-white rounded-lg hover:bg-[#EFA938] inline-block dark:bg-[#b36f23]"
        >
         Go to Training
        </Link>
      </div>
    );
  }
  return (
    <div className="w-full">
      <div className="w-full flex justify-between items-center">
        <h3 className="mb-2 text-gray-800 dark:text-gray-50">Test Progress</h3>
        <span className="text-sm text-muted-foreground text-gray-600 dark:text-gray-200">
          {Object.keys(answers).length} of {testData.totalQuestions} questions
          answered
        </span>
      </div>
      <Progress
        value={(Object.keys(answers).length / testData.totalQuestions) * 100}
        className="mb-2"
      />
      <div className="w-full">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50">{testData?.title}</h2>
          <span className="text-gray-700 dark:text-gray-300">
            Page {currentPage + 1} of {testData?.totalPages}
          </span>
        </div>
        <div className="flex flex-col gap-6 text-gray-900 dark:text-gray-100">
          {testData.questions.map((question, index) => (
            <MCQQuestion
              key={question._id}
              question={question}
              selectedAnswer={answers[question._id] || ''}
              index={currentPage * questionsPerPage + index}
              onAnswerSelect={handleAnswerSelect}
            />
          ))}
        </div>
        <div className="flex justify-between mt-6 items-center">
          <Button
            onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
            className={`px-4 py-2 text-base rounded-lg text-white 
                ${
                  currentPage === 0
                    ? "bg-gray-300 cursor-not-allowed dark:bg-gray-700"
                    : "bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"
                }
                dark:text-white`}
            disabled={currentPage === 0}
          >
            Previous
          </Button>
          <Button
            onClick={handleNextPage}
            className="px-6 py-3 text-base bg-[#EFA938] text-white rounded-lg hover:bg-[#EFA938] dark:bg-[#b36f23] dark:hover:bg-[#b36f23]"
          >
            {currentPage === testData.totalPages - 1 ? "Submit Test" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
